/**
 * Publish the service fee: a percentage of the food, between a floor and a cap.
 *
 * The server also keeps ₦100 after Paystack's fee on every order, raising the
 * fee on small baskets where the percentage alone would not cover processing,
 * so no schedule published here can make an order lose money. Delivery is not
 * charged until ChowDash dispatch is live, so it is not editable here.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { MinimalAlert, MinimalButton, MinimalInput } from '@ovasabi/ui-minimal'
import { z } from 'zod'
import { commandPayload, commands } from '../../lib/runtime'
import * as S from './OpsStyle'

/** Integer kobo on the wire. Naira typed in the form is converted below. */
const kobo = z.coerce.number().int().nonnegative().safe()

const feesSchema = z.object({
  version: z.coerce.number().int().nonnegative(),
  source: z.enum(['default', 'configured']),
  delivery_standard_minor: kobo,
  delivery_chowdash_minor: kobo,
  delivery_express_minor: kobo,
  delivery_recurring_minor: kobo,
  service_fee_minor: kobo,
  service_fee_raised_minor: kobo,
  service_fee_raised_from_minor: kobo,
  service_fee_bps: z.coerce.number().int().min(0).max(2000).default(0),
  service_fee_min_minor: kobo.default(0),
  service_fee_max_minor: kobo.default(0),
  service_fee_margin_minor: kobo.default(0),
})
type Fees = z.infer<typeof feesSchema>

const feeFields = [
  ['service_fee_min_minor', 'Minimum fee'],
  ['service_fee_max_minor', 'Maximum fee'],
] as const

/** "5" or "4.5" percent to basis points, without a float. */
export const percentToBps = (input: string): number => {
  const trimmed = input.trim()
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(trimmed)) {
    throw new Error('Enter the rate as a percentage, for example 5 or 4.5.')
  }
  const [whole, fraction = ''] = trimmed.split('.')
  const bps = Number(whole) * 100 + Number((fraction + '00').slice(0, 2))
  if (bps <= 0 || bps > 2000) throw new Error('The rate must be above 0% and at most 20%.')
  return bps
}

const bpsToPercent = (bps: number): string => (bps % 100 === 0 ? String(bps / 100) : (bps / 100).toFixed(2))

/**
 * Convert naira to whole kobo without a float.
 *
 * `nairaToKobo(500)` is 50000. A fractional naira amount is rejected rather
 * than rounded, because half a kobo is not an amount anyone can charge.
 */
export const nairaToKobo = (input: string): number => {
  const trimmed = input.trim()
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    throw new Error('Enter a positive amount with at most two decimal places.')
  }
  const [whole, fraction = ''] = trimmed.split('.')
  return Number(whole) * 100 + Number((fraction + '00').slice(0, 2))
}

const koboToNaira = (minor: number): string => {
  const whole = Math.trunc(minor / 100)
  const rest = String(minor % 100).padStart(2, '0')
  return rest === '00' ? String(whole) : `${whole}.${rest}`
}

export const OpsFeePanel = () => {
  const [fees, setFees] = useState<Fees | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const pending = useRef(false)

  const apply = (value: Fees) => {
    setFees(value)
    setForm({
      ...Object.fromEntries(feeFields.map(([key]) => [key, koboToNaira(value[key])])),
      rate: value.service_fee_bps > 0 ? bpsToPercent(value.service_fee_bps) : '',
    })
  }

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    setFees(null)
    void commands.marketplace
      .getPlatformFees()
      .then((current) => {
        if (active) apply(feesSchema.parse(commandPayload(current)))
      })
      .catch(() => {
        if (active) {
          setError('Could not load the current fees. Retry before editing.')
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [attempt])

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!fees || pending.current) return
    pending.current = true
    setBusy(true)
    setError('')
    setNotice('')
    try {
      // Validated locally first so a typo never becomes a published version.
      const bps = percentToBps(form.rate ?? '')
      const floor = nairaToKobo(form.service_fee_min_minor ?? '')
      const cap = nairaToKobo(form.service_fee_max_minor ?? '')
      if (cap < floor) throw new Error('The maximum fee cannot be below the minimum.')
      const confirmed = feesSchema.parse(
        commandPayload(
          await commands.marketplace.setPlatformFees({
            service_fee_bps: String(bps),
            service_fee_min_minor: String(floor),
            service_fee_max_minor: String(cap),
          }),
        ),
      )
      apply(confirmed)
      setNotice(
        `Fee version ${confirmed.version} saved. Orders already placed keep the fee they were charged.`,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the fees.')
    } finally {
      pending.current = false
      setBusy(false)
    }
  }

  return (
    <S.Card>
      <S.CardTitle>Order service fee</S.CardTitle>
      {loading && <p role="status">Loading fees…</p>}
      {error && (
        <MinimalAlert tone="danger" title="Fee change failed">
          {error}
          <MinimalButton disabled={busy} onClick={() => setAttempt((n) => n + 1)}>
            Reload fees
          </MinimalButton>
        </MinimalAlert>
      )}
      {notice && (
        <MinimalAlert tone="success" title="Saved">
          {notice}
        </MinimalAlert>
      )}
      {fees && !loading && (
        <S.Form onSubmit={save}>
          <S.Note>
            {fees.source === 'default'
              ? 'Server defaults — no fee version has been published yet'
              : `Fee version ${fees.version}`}{' '}
            · applies to new orders only.
          </S.Note>
          {fees.service_fee_bps === 0 && (
            <MinimalAlert tone="warning" title="Flat fee in use">
              The live schedule charges a flat {koboToNaira(fees.service_fee_minor)} naira, which stops covering
              Paystack’s fee on baskets above about ₦10,000. Publish a rate below to fix that.
            </MinimalAlert>
          )}
          <S.FormGrid>
            <MinimalInput
              required
              label="Rate (% of the food)"
              value={form.rate ?? ''}
              inputMode="decimal" inputSize="md"
              disabled={busy}
              onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
            />
            {feeFields.map(([key, label]) => (
              <MinimalInput
                key={key}
                required
                label={`${label} (₦)`}
                value={form[key] ?? ''}
                inputMode="decimal" inputSize="md"
                disabled={busy}
                onChange={(e) =>
                  setForm((f) => ({ ...f, [key]: e.target.value }))
                }
              />
            ))}
          </S.FormGrid>
          <S.Note>
            Charged once per kitchen order, including planned meals. A two-kitchen cart pays twice.
            Whatever you publish, the server raises the fee on small baskets so ChooseChow keeps at
            least ₦{koboToNaira(fees.service_fee_margin_minor || 10000)} after Paystack’s fee, and rounds
            every fee up to the nearest ₦50. Existing orders keep their saved fees.
          </S.Note>
          <MinimalButton type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save fee version'}
          </MinimalButton>
        </S.Form>
      )}
    </S.Card>
  )
}
